import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import 'react-dates/lib/css/_datepicker.css';

import Editor from '@components/editor/Editor';
import LoaderComponent from '@components/LoaderComponent';
import SampleReply from '@components/SampleReply';
import { TaskPostSkeleton } from '@components/TaskSkeletons';
import { toastError, toastSuccess, toastWarning } from '@components/Toast';

import { SelectedFileViewComponent } from '@features/task/components/TaskManageComponents/SelectedFileViewComponent';

import { clearAllChats } from '@store/actions/ChatAction';

import conversationService from '@services/conversation-service';

import ApiClient from '@lib/apiClient';

import { _getKey, addImageSuffix, printApiError, formatHtmlWithStyles } from '@utils/Util';

const SampleCommentPanel = ({
	orderId,
	postTypeFilter,
	id,
	topic,
	productId,
	sampleRequestId,
	getPostUrl,
	brandName,
	addPostUrl,
	brandId,
	postTypeId,
	isProject,
	styleList
}) => {
	const newPost = useSelector((store) => store.chatReducer.newMessage);
	const dropdownRef = useRef(null);
	const searchRef = useRef(null);
	const postInputRef = useRef(null);
	const dispatch = useDispatch();
	const [addComment, setAddComment] = useState(false);
	const [state, setState] = useState({
		loading: false,
		loadingPosts: false,
		loadingDetails: false,
		taskId: id,
		orderId: orderId,
		task: {},
		tempTask: {},
		showAddNewMemberModal: false,
		orderMemberList: [],
		editDescription: false,
		userInfo: {},
		posts: [],
		createPostFlag: false,
		post: '',
		selectedFiles: [],
		postType: '',
		startDate: null,
		endDate: null,
		focusedInput: null,
		postPageSize: 10,
		postTypeFilter: postTypeFilter,
		postPageNumber: 0,
		postHasNext: true,
		totalElements: 0,
		mentionToUser: [],
		selectedMentionedUser: [],
		nitexMentioned: true,
		brandMentioned: false,
		mentionToUsers: [],
		mentionSearch: '',
		highlightId: null,
		selectedPostTag: topic
	});
	const [, setReadOnly] = useState(true);
	const location = useLocation();
	const [editorContent, setEditorContent] = useState(null);
	const [mentionIds, setMentionIds] = useState([]);
	const [folderStyleList, setFolderStyleList] = useState([]);

	useEffect(() => {
		if (styleList) {
			let tmpData = styleList.map((item) => ({
				text: `"#${item?.productResponse?.referenceNumber || item?.referenceNumber}"`,
				value: item?.productResponse?.referenceNumber || item?.referenceNumber,
				id: item?.productResponse?.id || item?.id,
				designDocuments: item?.productResponse?.designDocuments || item?.designDocuments,
				isSet: item?.productResponse?.isSet || item?.isSet,
				name: item?.productResponse?.name || item?.name
			}));
			setFolderStyleList(tmpData);
		}
	}, [styleList]);

	const scrollDownInChatBody = () => {
		let container = document.getElementById('taskPostScroll');
		if (container) {
			let o1 = container.scrollHeight - container.clientHeight;
			let elem = document.getElementById('taskPostScroll');
			elem.scrollTop = o1;
		}
	};

	const handlePostScroll = async () => {
		const wrappedElement = document.getElementById('taskPostScroll');
		if (wrappedElement.scrollTop === 0) {
			let { posts, postPageNumber, postHasNext, loadingPosts } = state;
			if (postHasNext && !loadingPosts && posts.length) {
				fetchTaskPosts(postPageNumber + 1);
			}
		}
	};

	const selectedUserToMention = (value) => {
		const { selectedMentionedUser, mentionToUser } = state;
		if (mentionToUser && mentionToUser.length > 0) {
			const mentionedUsers = selectedMentionedUser;
			const selectedUser = mentionToUser.filter((el) => el.email === value);
			if (selectedUser[0]) {
				mentionedUsers.push(selectedUser[0]);
				setState((prev) => ({
					...prev,
					selectedMentionedUser: mentionedUsers,
					mentionToUser: [],
					mentionSearch: ''
				}));
			}
		}
	};

	const onDismissSelection = (userId) => {
		const { selectedMentionedUser } = state;
		const userDismiss = selectedMentionedUser;
		const index = selectedMentionedUser.findIndex((el) => el.id === userId);
		userDismiss.splice(index, 1);
		setState((prev) => ({
			...prev,
			selectedMentionedUser: userDismiss,
			mentionToUser: [],
			mentionSearch: ''
		}));
	};

	const handleMentionTo = async (e) => {
		setState((prev) => ({
			...prev,
			mentionSearch: e.target.value
		}));
		if (e.target.value) {
			const { data } = await conversationService.getSearchedUser(e.target.value);
			let newData = data.filter(
				(user) => !state.selectedMentionedUser.some((item) => user.email === item.email)
			);
			setState((prev) => ({ ...prev, mentionToUser: newData }));
		}
	};

	useEffect(() => {
		scrollDownInChatBody();
		document.body.addEventListener('keydown', handleKeyDown);
		setState((prev) => ({ ...prev, loadingDetails: true, loadingPosts: true }));
		let userInfo = localStorage.getItem('userInfo');
		if (userInfo) {
			userInfo = JSON.parse(userInfo);
			setState((prev) => ({ ...prev, userInfo }));
		}
		dispatch(clearAllChats());
		fetchTaskPosts();
		fetchMembers();
		const params = new URLSearchParams(location.search);
		if (params.get('postId') !== undefined) {
			setState((prev) => ({ ...prev, highlightId: parseInt(params.get('postId')) }));
		}

		return () => {
			document.body.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	useEffect(() => {
		const oldPosts = [...state.posts];
		if (newPost?.text && state.userInfo?.id !== newPost?.postedBy.id) {
			if (newPost?.parentPostId) {
				const index = oldPosts.findIndex((post) => post.id === newPost?.parentPostId);
				oldPosts[index].commentList.push(newPost);
			} else {
				oldPosts.push(newPost);
			}
			setState((prev) => ({ ...prev, posts: oldPosts }));
			scrollDownInChatBody();
			toastSuccess('New message received');
		}
		setTimeout(() => {
			dispatch(clearAllChats());
		}, 1000);
	}, [newPost]);

	useEffect(() => {
		fetchTaskPosts();
	}, [state.selectedPostTag]);

	const handleKeyDown = (event) => {
		if (
			document.activeElement.getAttribute('class') === 'outline-none mention-search-box' ||
			document.activeElement.getAttribute('class') === 'single-item-focus'
		) {
			const isUp = event.key === 'ArrowUp';
			const isDown = event.key === 'ArrowDown';
			const inputIsFocused = document.activeElement === searchRef.current;
			const resultsItems = Array.from((dropdownRef.current && dropdownRef.current.children) || []);
			const activeResultIndex = resultsItems.findIndex((child) => {
				return child.querySelector('span') === document.activeElement;
			});

			if (isUp) {
				if (inputIsFocused) {
					resultsItems[resultsItems.length - 1].querySelector('span').focus();
				} else if (resultsItems[activeResultIndex - 1]) {
					resultsItems[activeResultIndex - 1].querySelector('span').focus();
				} else {
					searchRef.current && searchRef.current.focus();
				}
			}

			if (isDown) {
				if (inputIsFocused) {
					resultsItems[0].querySelector('span').focus();
				} else if (resultsItems[activeResultIndex + 1]) {
					resultsItems[activeResultIndex + 1].querySelector('span').focus();
				} else {
					searchRef.current && searchRef.current.focus();
				}
			}
		}
	};

	const fetchTaskPosts = async (page = 0) => {
		setState((prev) => ({ ...prev, loading: true }));
		let { postPageSize, posts, selectedPostTag } = state;
		const targetId = productId ? productId : sampleRequestId;
		let container;
		let o1;
		if (page === 0) {
			setState((prev) => ({ ...prev, posts: [] }));
		}
		if (page > 0) {
			container = document.getElementById('taskPostScroll');
			o1 = container.scrollHeight - container.clientHeight;
		}
		let params = `${productId ? '' : '/'}${targetId}?page=${page}&size=${postPageSize}&sort=id,desc`;
		if (selectedPostTag) {
			params += `&postTag=${selectedPostTag}`;
		}
		if (targetId !== undefined) {
			await ApiClient.GET(getPostUrl, params)
				.then(({ data }) => {
					setState((prev) => ({
						...prev,
						loadingPosts: false,
						loading: false
					}));
					if (data.data) {
						let result = [...data.data];
						result.reverse();
						result = result.map((post) => {
							post.key = _getKey();
							return post;
						});
						setState((prev) => ({
							...prev,
							posts: page === 0 ? result : [...result, ...posts],
							postPageNumber: page,
							totalElements: data.totalElements,
							postHasNext: result.length && result.length === postPageSize
						}));
					}
					if (page > 0) {
						setTimeout(() => {
							let newContainer = document.getElementById('taskPostScroll');
							let n1 = newContainer.scrollHeight - newContainer.clientHeight;
							let elem = document.getElementById('taskPostScroll');
							elem.scrollTop = n1 - o1;
						}, 100);
					} else {
						setTimeout(() => {
							scrollDownInChatBody();
						}, 100);
					}
				})
				.catch((error) => {
					setState((prev) => ({ ...prev, loadingPosts: false, loading: false }));
					printApiError(error);
				});
		}
	};

	const onMultipleFileSelect = async (e, docType) => {
		let { selectedFiles } = state;
		let files = Array.from(e.target.files);
		files.map((item) => {
			let data = {
				name: item.name,
				docMimeType: item.type,
				documentType: docType,
				print: false,
				base64Str: ''
			};
			let reader = new FileReader();
			reader.readAsDataURL(item);
			reader.onload = () => {
				data.base64Str = reader.result;
				selectedFiles.push(data);
				setState((prev) => ({ ...prev, selectedFiles, createPostFlag: true }));
			};
		});
	};

	const removeFile = (index) => {
		let { selectedFiles } = state;
		setState((prev) => ({
			...prev,
			selectedFiles: selectedFiles.filter((file, i) => i !== index)
		}));
	};

	const fetchMembers = async () => {
		let params = '?isNitexUser=true&page=0&size=500';
		if (brandId) {
			params += `&brandId=${brandId}`;
		}
		await ApiClient.GET('getUserSuggestions', params)
			.then(({ data }) => {
				setState((prev) => ({
					...prev,
					orderMemberList: data
				}));
			})
			.catch((error) => {
				printApiError(error);
			});
	};

	const handlePost = async () => {
		let newComment = editorContent === null ? '' : editorContent.toString().replace('<p></p>', '').trim();
		if (!newComment.trim().length > 0) {
			toastWarning('Please provide a description!');
			return false;
		}
		await submitPost();
	};

	const submitPost = async () => {
		let { selectedFiles, posts, selectedMentionedUser, nitexMentioned, brandMentioned } = state;
		const formattedHtml = formatHtmlWithStyles(editorContent, folderStyleList);
		const body = {
			documentDTOList: selectedFiles,
			[postTypeId]: parseInt(sampleRequestId),
			postType: postTypeFilter,
			text: formattedHtml,
			taggedUserIdList: mentionIds
		};

		if (
			['PRODUCT_SAMPLE_COMMENT', 'PRODUCT_QUOTE_COMMENT', 'PRODUCT_DEVELOPMENT_COMMENT'].includes(
				topic
			) &&
			productId
		) {
			body.postTag = state.selectedPostTag;
			body.productId = productId;
			body.postType = 'PRODUCT_DEVELOPMENT_COMMENT';
		}

		body.recipientList = [];
		if (nitexMentioned) body.recipientList.push({ recipientType: 'NITEX' });

		if (brandMentioned && brandName !== 'Nitex') body.recipientList.push({ recipientType: 'BUYER' });

		if (selectedMentionedUser) {
			selectedMentionedUser.map((user) => {
				const userIdMap = {
					recipientType: 'INDIVIDUAL',
					userId: user.id
				};
				body.recipientList.push(userIdMap);
			});
		}
		if (body.recipientList.length === 0) {
			toastError('At least one mention nedded');
			setState((prev) => ({ ...prev, nitexMentioned: true, brandMentioned: true }));
			return;
		}

		if (isProject) {
			body.projectTaskId = productId;
			delete body.quoteRequestId;
			delete body.undefined;
		}

		setState((prev) => ({ ...prev, loading: true }));
		await ApiClient.POST(addPostUrl, body)
			.then(({ data }) => {
				let newPosts = [...posts];
				if (data.payload) {
					newPosts.push(data.payload);
				}
				setState((prev) => ({
					...prev,
					totalElements: state.totalElements + 1,
					post: '',
					selectedFiles: [],
					posts: newPosts,
					createPostFlag: false,
					loading: false
				}));
				postInputRef.current = '';
				setAddComment(false);
				scrollDownInChatBody();
			})
			.catch((error) => {
				setState((prev) => ({ ...prev, loading: false }));
				printApiError(error);
			});
	};

	const toggleLoader = (flag) => {
		setState((prev) => ({
			...prev,
			loading: flag
		}));
	};

	const {
		orderMemberList,
		userInfo,
		posts,
		post,
		selectedFiles,
		loadingPosts,
		postPageNumber,
		totalElements,
		selectedMentionedUser,
		nitexMentioned,
		brandMentioned,
		mentionToUser,
		mentionSearch,
		highlightId
	} = state;
	return (
		<LoaderComponent loading={state.loading}>
			<Modal.Header className="p-2">
				<h3 className="regular-14 gray_dark_02 weight-500 m-1">Conversation ({totalElements}) </h3>
			</Modal.Header>
			{postPageNumber === 0 && loadingPosts ? (
				<></>
			) : (
				<Modal.Body className="custom-scrollbar pt-0" id="taskPostScroll" onScroll={handlePostScroll}>
					<div className="conversation-container">
						{[
							'PRODUCT_SAMPLE_COMMENT',
							'PRODUCT_QUOTE_COMMENT',
							'PRODUCT_DEVELOPMENT_COMMENT'
						].includes(topic) && (
							<div className="conversation-filter-dropdown bg-white">
								<select
									className="w-100 mb-12"
									name="postTag"
									id="postTag"
									value={state.selectedPostTag}
									onChange={(e) =>
										setState((prev) => ({ ...prev, selectedPostTag: e.target.value }))
									}
								>
									<option value={''}>All</option>
									<option value="PRODUCT_DEVELOPMENT_COMMENT">Design</option>
									<option value="PRODUCT_SAMPLE_COMMENT">Sample</option>
									<option value="PRODUCT_QUOTE_COMMENT">Costing</option>
								</select>
							</div>
						)}
						{posts?.map((post) => {
							if (
								post.postType === 'SAMPLE_AUTO_GENERATED_COMMENT' ||
								post.postType === 'QUOTE_AUTO_GENERATED_COMMENT'
							) {
								return (
									<div
										className={`${highlightId === post.id ? 'highlight-wrapper' : ''}`}
										key={`sample_${post.id}`}
									>
										<SampleReply
											selectedPostTag={state.selectedPostTag}
											topic={topic}
											postData={post}
											key={post.id}
											orderMemberList={orderMemberList}
											toggleLoader={toggleLoader}
											userInfo={userInfo}
											commentType="SYSTEM_GENERATED"
											commentFrom="OTHER"
										/>
									</div>
								);
							} else {
								return (
									<div
										className={`${highlightId === post.id ? 'highlight-wrapper' : ''}`}
										key={`sample_reply_${post.id}`}
									>
										<SampleReply
											selectedPostTag={state.selectedPostTag}
											topic={topic}
											postData={post}
											key={post.id}
											orderMemberList={orderMemberList}
											toggleLoader={toggleLoader}
											userInfo={userInfo}
											commentFrom="OTHER"
										/>{' '}
									</div>
								);
							}
						})}
						{posts?.length === 0 && !loadingPosts && (
							<div className="no-comments">No comment found</div>
						)}
					</div>
				</Modal.Body>
			)}

			{loadingPosts && <TaskPostSkeleton />}
			<Modal.Footer
				className={`modal-footer-fixed posting-option ${
					selectedFiles?.length > 0 ? 'file-exist' : ''
				} ${addComment ? `open` : ``}`}
			>
				<div className="tag-person to-fixed">
					<div className="tag-items">
						<p>TO</p>
						{nitexMentioned && (
							<p className="single-tag">
								<span>Nitex</span>
								<img
									src={`${process.env.PUBLIC_URL}/icons/close-14.svg`}
									onClick={() => setState((prev) => ({ ...prev, nitexMentioned: false }))}
								/>
							</p>
						)}
						{brandMentioned && brandName !== 'Nitex' && (
							<p className="single-tag">
								<span>{brandName}</span>
								<img
									src={`${process.env.PUBLIC_URL}/icons/close-14.svg`}
									onClick={() => setState((prev) => ({ ...prev, brandMentioned: false }))}
								/>
							</p>
						)}
						{selectedMentionedUser &&
							selectedMentionedUser.map((user, i) => {
								return (
									<p className="single-tag" key={`tag_${i}`}>
										<span>{user.email}</span>
										<img
											src={`${process.env.PUBLIC_URL}/icons/close-14.svg`}
											onClick={() => onDismissSelection(user.id)}
										/>
									</p>
								);
							})}
						<div>
							<div className="metion-person-suggetion scroll-y-label">
								<ul className="scroll-y-label shadow-lg" ref={dropdownRef}>
									{!nitexMentioned && mentionSearch !== '' && (
										<li
											key={'nitex'}
											className="cursor-pointer px-3 py-1"
											onClick={() => {
												setState((prev) => ({
													...prev,
													nitexMentioned: true,
													mentionToUser: [],
													mentionSearch: ''
												}));
											}}
										>
											<span className="single-item-focus" tabIndex={0}>
												Nitex
											</span>
										</li>
									)}
									{!brandMentioned && mentionSearch !== '' && brandName !== 'Nitex' && (
										<li
											key={'buyer'}
											className="cursor-pointer"
											onClick={() => {
												setState((prev) => ({
													...prev,
													brandMentioned: true,
													mentionToUser: [],
													mentionSearch: ''
												}));
											}}
										>
											<span className="single-item-focus" tabIndex={0}>
												{brandName}
											</span>
										</li>
									)}

									{mentionSearch.length > 0 &&
										mentionToUser.map((user) => {
											return (
												<li
													key={user.id}
													className="cursor-pointer px-3 py-1"
													onClick={() => selectedUserToMention(user.email)}
													onKeyPress={(e) => {
														if (e.key === 'Enter') {
															selectedUserToMention(user.email);
														}
													}}
												>
													<span className="single-item-focus" tabIndex={0}>
														{user.email}
													</span>
												</li>
											);
										})}
								</ul>
							</div>
							<input
								onClick={() => {
									setReadOnly(true);
								}}
								className="outline-none mention-search-box"
								value={mentionSearch}
								placeholder="Search with email"
								type="text"
								autoComplete="off"
								onChange={handleMentionTo}
								ref={searchRef}
							/>
						</div>
					</div>
				</div>

				<div className="d-flex w-100">
					<div className="user-photo">
						{userInfo && userInfo.profilePicDocument ? (
							<img src={addImageSuffix(userInfo.profilePicDocument.docUrl, '_xicon')} />
						) : (
							<img src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`} />
						)}
					</div>
					{!addComment ? (
						<textarea
							placeholder="Write comment..."
							onClick={() => {
								setAddComment(true);
								setReadOnly(false);
							}}
						/>
					) : (
						<Editor
							memberList={orderMemberList?.map((user) => {
								return {
									memberId: user.id,
									value: user.name
								};
							})}
							setEditorContent={setEditorContent}
							setMentionIds={setMentionIds}
							styleList={folderStyleList}
						/>
					)}
				</div>

				<div className={`files-n-photos custom-scrollbar ${selectedFiles.length ? `open` : ``}`}>
					{selectedFiles.map((file, i) => {
						return (
							<SelectedFileViewComponent
								showRemoveOption={true}
								file={file}
								key={i}
								index={i}
								remove={removeFile}
							/>
						);
					})}
				</div>

				<div className="post-actions">
					<div className="attachment cursor-pointer">
						<label htmlFor="input-file">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.5 22C9.46 22 7 19.54 7 16.5L7 6C7 3.79 8.79 2 11 2C13.21 2 15 3.79 15 6L15 14.5C15 15.88 13.88 17 12.5 17C11.12 17 10 15.88 10 14.5L10 7L12 7L12 14.59C12 15.14 13 15.14 13 14.59L13 6C13 4.9 12.1 4 11 4C9.9 4 9 4.9 9 6L9 16.5C9 18.43 10.57 20 12.5 20C14.43 20 16 18.43 16 16.5L16 7L18 7L18 16.5C18 19.54 15.54 22 12.5 22Z"
									fill="#595959"
								/>
							</svg>
						</label>
						<input
							id="input-file"
							type="file"
							name="selectedFiles"
							onChange={(e) => onMultipleFileSelect(e, 'ACCESSORIES_DESIGN')}
							multiple
						/>
					</div>
					<div className="save-note">
						<button
							className="button text size32"
							onClick={() => {
								setState((prev) => ({
									...prev,
									post: '',
									selectedFiles: [],
									postType: '',
									createPostFlag: false
								}));
								postInputRef.current = '';
								setAddComment(false);
							}}
						>
							<span className="gray_dark_02">CANCEL</span>
						</button>
						<button
							className={`button mr-0 ${post.length ? `brand-bg-color` : ``}`}
							onClick={handlePost}
						>
							POST
						</button>
					</div>
				</div>
			</Modal.Footer>
		</LoaderComponent>
	);
};
export default SampleCommentPanel;
